/**
 * Rota `/overview` — Fase 2 PR-B do TD26 roadmap.
 *
 * Tela home alternativa pro operador. Diferente do Cockpit (foco em ação:
 * mapa + tabela filtrável), Overview é foco em **status global agora**:
 * KPIs grandes + breakdown por OLT + alertas recentes.
 *
 * Sections:
 * 1. Hero KPIs — total / online% / bad / bad<5m (com cor por threshold)
 * 2. Breakdown por OLT — health bars empilhadas (reuso useOltStats / OltCard)
 * 3. Alertas recentes — top 10 ONUs que entraram em bad < 5min
 *
 * Source:
 * - `useStatsQuery` → /api/v2/stats (refetch 30s, Redis-cached)
 * - `useOltStats` → registry + snapshot agregado client-side (compartilhado
 *   com /olts e /cockpit)
 * - `useSnapshot({status:'bad'})` → lista pra alertas (compartilhado com
 *   cockpit bootstrap progressive)
 */

import { AlertList } from '@/features/overview/AlertList';
import { OltsBreakdown } from '@/features/overview/OltsBreakdown';
import { OverviewKpis } from '@/features/overview/OverviewKpis';
import { useStatsQuery } from '@/features/stats/useStatsQuery';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/overview')({
  component: OverviewPage,
});

function OverviewPage() {
  const statsQ = useStatsQuery();

  if (statsQ.isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-sm text-fg-subtle">
        Carregando overview…
      </div>
    );
  }

  if (statsQ.isError || !statsQ.data) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-8 text-sm">
        <p className="text-status-critical">Falha ao carregar overview.</p>
        <p className="text-fg-subtle">Reload da página tenta novamente.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto p-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-fg">Overview</h1>
        <p className="mt-1 text-sm text-fg-subtle">Status global da rede. Atualiza a cada 30s.</p>
      </div>

      {/* Hero KPIs */}
      <OverviewKpis stats={statsQ.data} />

      {/* 2 colunas: breakdown OLT + alertas */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <OltsBreakdown />
        <AlertList />
      </div>
    </div>
  );
}
